import React, { useEffect, useMemo, useState } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { GetYearlyTaskReport } from "../../AdminServices/AdminReportService";

const getCreatedCount = (row) => row?.totalTasks ?? row?.createdTasks ?? 0;
const getUnderReviewCount = (row) =>
	row?.underReviewTasks ?? row?.completedTasks ?? 0;

const AdminReport = () => {
	const currentYear = new Date().getFullYear();
	const [selectedYear, setSelectedYear] = useState(String(currentYear));
	const [reportRows, setReportRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const navigate = useNavigate();

	useEffect(() => {
		const fetchYearlyReport = async () => {
			try {
				setLoading(true);
				setError("");

				const res = await GetYearlyTaskReport({ year: selectedYear });
				setReportRows(res?.data?.taskReport || []);
			} catch (err) {
				console.error("Failed to fetch yearly task report", err);
				setReportRows([]);
				setError("Unable to load yearly task report");
			} finally {
				setLoading(false);
			}
		};

		fetchYearlyReport();
	}, [selectedYear]);

	const { monthlyRows, totalsRow, chartData } = useMemo(() => {
		const rows = Array.isArray(reportRows) ? reportRows : [];
		const totals = rows.find((row) => row.monthNo === 13) || null;
		const months = rows.filter((row) => row.monthNo !== 13);

		return {
			monthlyRows: months,
			totalsRow: totals,
			chartData: months.map((row) => ({
				month: row.monthName,
				Created: getCreatedCount(row),
				Closed: row.closedTasks,
				"Under Review": getUnderReviewCount(row),
				Open: row.openTasks,
				"In Progress": row.inProgressTasks,
				Hold: row.holdTasks,
			})),
		};
	}, [reportRows]);

	const yearOptions = Array.from({ length: 6 }, (_, index) =>
		String(currentYear - index)
	);

	const summaryCards = [
		{
			label: "Created Tasks",
			value: getCreatedCount(totalsRow),
		},
		{
			label: "Closed Tasks",
			value: totalsRow?.closedTasks ?? 0,
		},
		{
			label: "Under Review Tasks",
			value: getUnderReviewCount(totalsRow),
		},
		{
			label: "Open Tasks",
			value: totalsRow?.openTasks ?? 0,
		},
		{
			label: "In Progress Tasks",
			value: totalsRow?.inProgressTasks ?? 0,
		},
		{
			label: "Hold Tasks",
			value: totalsRow?.holdTasks ?? 0,
		},
		{
			label: "Closure Rate",
			value: getCreatedCount(totalsRow)
				? `${Math.round((totalsRow.closedTasks / getCreatedCount(totalsRow)) * 100)}%`
				: "0%",
		},
	];

	return (
		<div className="p-6 bg-gray-50 min-h-screen">
			{/* Tabs (Channels, Agents, etc.) */}
			<div className="flex border-b border-gray-200 mb-6">
				{[
					"Reports",
					"Daily Task Report",
					"Orbit Task Report",
					"Tickets",
					"Satisfaction",
					"Tags",
				].map((tab) => (
					<button
						key={tab}
						onClick={() => {
							if (tab === "Orbit Task Report") {
								navigate("/Admin-Report/AI-Report");
							} else if (tab === "Daily Task Report") {
								navigate("/Admin-Report/DailyTask-AI-Report");
							} else {
								navigate(`/Admin-Report`);
							}
						}}
						className={`px-6 py-3 text-sm font-medium border-b-2 ${
							tab === "Reports"
								? "border-blue-600 text-blue-600"
								: "border-transparent text-gray-500 hover:text-blue-600"
						}`}
					>
						{tab}
					</button>
				))}
			</div>

			<div className="bg-white border rounded-lg p-4 md:p-5 mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
				<div>
					<h2 className="text-lg font-semibold text-gray-800">
						Yearly Task Report
					</h2>
					<p className="text-sm text-gray-500 mt-1">
						Month-wise admin task status overview for the selected year
					</p>
				</div>

				<div className="w-full md:w-48">
					<label className="block text-sm text-gray-600 mb-1">Year</label>
					<select
						value={selectedYear}
						onChange={(e) => setSelectedYear(e.target.value)}
						className="w-full border border-gray-200 px-3 py-2 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
					>
						{yearOptions.map((year) => (
							<option key={year} value={year}>
								{year}
							</option>
						))}
					</select>
				</div>
			</div>

			{/* Summary Cards */}
			<div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
				{summaryCards.map((card) => (
					<div
						key={card.label}
						className="bg-white border rounded-lg p-4 text-center"
					>
						<p className="text-gray-500 text-sm">{card.label}</p>
						<p className="text-2xl font-semibold mt-1">{card.value}</p>
					</div>
				))}
			</div>

			{/* Chart Card */}
			<div className="bg-white border rounded-lg p-6">
				<div className="flex justify-between items-center mb-4">
					<div>
						<h3 className="text-gray-700 font-medium">Task Status Trend</h3>
						<div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
							<span className="flex items-center gap-1">
								<span className="w-3 h-3 bg-blue-500 rounded-full"></span>
								Created
							</span>
							<span className="flex items-center gap-1">
								<span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
								Closed
							</span>
							<span className="flex items-center gap-1">
								<span className="w-3 h-3 bg-teal-400 rounded-full"></span>
								Under Review
							</span>
							<span className="flex items-center gap-1">
								<span className="w-3 h-3 bg-amber-400 rounded-full"></span>
								Open
							</span>
							<span className="flex items-center gap-1">
								<span className="w-3 h-3 bg-violet-500 rounded-full"></span>
								In Progress
							</span>
							<span className="flex items-center gap-1">
								<span className="w-3 h-3 bg-rose-500 rounded-full"></span>
								Hold
							</span>
						</div>
					</div>

					<div className="text-sm text-gray-500">
						{loading ? "Loading..." : `${selectedYear} overview`}
					</div>
				</div>

				<div className="w-full h-80">
					{loading ? (
						<div className="h-full flex items-center justify-center text-gray-500">
							<Loader2 className="animate-spin mr-2" size={18} />
							Loading yearly report...
						</div>
					) : chartData.length > 0 ? (
						<ResponsiveContainer width="100%" height="100%">
							<LineChart
								data={chartData}
								margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
							>
								<CartesianGrid strokeDasharray="3 3" vertical={false} />
								<XAxis dataKey="month" axisLine={false} tickLine={false} />
								<YAxis allowDecimals={false} axisLine={false} tickLine={false} />
								<Tooltip />
								<Legend />
								<Line
									type="monotone"
									dataKey="Created"
									stroke="#2563eb"
									strokeWidth={2.5}
								/>
								<Line
									type="monotone"
									dataKey="Closed"
									stroke="#10b981"
									strokeWidth={2.5}
								/>
								<Line
									type="monotone"
									dataKey="Under Review"
									stroke="#2dd4bf"
									strokeWidth={2.5}
								/>
								<Line
									type="monotone"
									dataKey="Open"
									stroke="#f59e0b"
									strokeWidth={2.5}
								/>
								<Line
									type="monotone"
									dataKey="In Progress"
									stroke="#8b5cf6"
									strokeWidth={2.5}
								/>
								<Line
									type="monotone"
									dataKey="Hold"
									stroke="#f43f5e"
									strokeWidth={2.5}
								/>
							</LineChart>
						</ResponsiveContainer>
					) : (
						<div className="h-full flex items-center justify-center text-gray-400">
							No yearly task data available
						</div>
					)}
				</div>
			</div>

			{error && (
				<div className="mt-4 text-sm font-medium text-red-500">{error}</div>
			)}

			<div className="bg-white border rounded-lg p-6 mt-6">
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-gray-700 font-medium">Monthly Breakdown</h3>
					{totalsRow && (
						<p className="text-sm text-gray-500">
							Year total: {getCreatedCount(totalsRow)} tasks
						</p>
					)}
				</div>

				<div className="overflow-x-auto">
					<table className="min-w-full text-sm">
						<thead>
							<tr className="border-b text-left text-gray-500">
								<th className="py-3 pr-4 font-medium">Month</th>
								<th className="py-3 px-4 font-medium">Created</th>
								<th className="py-3 px-4 font-medium">Closed</th>
								<th className="py-3 px-4 font-medium">Under Review</th>
								<th className="py-3 px-4 font-medium">Hold</th>
								<th className="py-3 px-4 font-medium">Open</th>
								<th className="py-3 px-4 font-medium">In Progress</th>
							</tr>
						</thead>
						<tbody>
							{monthlyRows.map((row) => (
								<tr key={row.monthNo} className="border-b last:border-b-0">
									<td className="py-3 pr-4 font-medium text-gray-700">
										{row.monthName}
									</td>
									<td className="py-3 px-4">{getCreatedCount(row)}</td>
									<td className="py-3 px-4">{row.closedTasks}</td>
									<td className="py-3 px-4">{getUnderReviewCount(row)}</td>
									<td className="py-3 px-4">{row.holdTasks}</td>
									<td className="py-3 px-4">{row.openTasks}</td>
									<td className="py-3 px-4">{row.inProgressTasks}</td>
								</tr>
							))}
							{totalsRow && (
								<tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-gray-800">
									<td className="py-3 pr-4">{totalsRow.monthName}</td>
									<td className="py-3 px-4">{getCreatedCount(totalsRow)}</td>
									<td className="py-3 px-4">{totalsRow.closedTasks}</td>
									<td className="py-3 px-4">{getUnderReviewCount(totalsRow)}</td>
									<td className="py-3 px-4">{totalsRow.holdTasks}</td>
									<td className="py-3 px-4">{totalsRow.openTasks}</td>
									<td className="py-3 px-4">{totalsRow.inProgressTasks}</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default AdminReport;
